import React, { Component } from 'react';
import { Box, Button, Typography } from '@mui/material';
import enums from 'helpers/const';
import { ethers } from 'ethers';
import '../style.css';

class TabWrapper extends Component {
  state = {
    hasMetaMask: false,
    isWalletConnected: false,
    isCorrectChain: false,
    error: null,
  };

  componentDidMount() {
    this.checkWalletConnection();
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', this.handleAccountsChanged);
      window.ethereum.on('chainChanged', this.handleChainChanged);
    }
  }

  componentWillUnmount() {
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', this.handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', this.handleChainChanged);
    }
  }

  checkWalletConnection = async () => {
    const hasMetaMask = typeof window.ethereum !== 'undefined';
    this.setState({ hasMetaMask });

    if (hasMetaMask) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        const isWalletConnected = accounts.length > 0;
        this.setState({ isWalletConnected });

        if (isWalletConnected) {
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          const network = await provider.getNetwork();
          const expectedChainId = parseInt(this.props.chainId || '0', 10);

          const isCorrectChain = network.chainId === expectedChainId;
          this.setState({ isCorrectChain });
        }
      } catch (error) {
        console.error('Error checking wallet connection:', error);
        this.setState({ error: error.message });
      }
    }
  };

  handleAccountsChanged = (accounts) => {
    const isWalletConnected = accounts.length > 0;
    this.setState({ isWalletConnected });
    this.checkWalletConnection();
  };

  handleChainChanged = () => {
    this.checkWalletConnection();
  };

  connectWallet = async () => {
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      this.checkWalletConnection();
    } catch (error) {
      console.error('Error connecting wallet:', error);
      this.setState({ error: error.message });
    }
  };

  switchChain = async () => {
    try {
      const chainId = parseInt(this.props.chainId || '0', 10);

      if (isNaN(chainId) || chainId === 0) {
        throw new Error('Invalid chain ID');
      }
      const chainIdHex = '0x' + chainId.toString(16);

      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }],
      });
    } catch (error) {
      if (error.code === 4902) {
        try {
          const chainId = parseInt(this.props.chainId || '0', 10);

          if (isNaN(chainId) || chainId === 0) {
            throw new Error('Invalid chain ID');
          }
          const chainIdHex = '0x' + chainId.toString(16);

          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: chainIdHex,
                chainName: chainId === 56 ? 'BSC' : 'Polygon',
                nativeCurrency:
                  chainId === 56
                    ? {
                        name: 'BNB',
                        symbol: 'BNB',
                        decimals: 18,
                      }
                    : {
                        name: 'MATIC',
                        symbol: 'MATIC',
                        decimals: 18,
                      },
                rpcUrls: chainId === 56 ? ['https://bsc-dataseed.binance.org/'] : ['https://polygon-rpc.com/'],
                blockExplorerUrls: chainId === 56 ? ['https://bscscan.com/'] : ['https://polygonscan.com/'],
              },
            ],
          });
        } catch (addError) {
          console.error('Error adding chain:', addError);
          this.setState({ error: addError.message });
        }
      } else {
        console.error('Error switching chain:', error);
        this.setState({ error: error.message });
      }
    }
  };

  render() {
    const { hasMetaMask, isWalletConnected, isCorrectChain, error } = this.state;
    const { children, daoOrganizerAddress } = this.props;

    if (!hasMetaMask) {
      return (
        <Box textAlign="center" p={3}>
          <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
            Please install MetaMask to access this content
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#05e4bc',
              '&:hover': {
                backgroundColor: '#04c9a5',
              },
              borderRadius: '12px',
              textTransform: 'none',
              py: 1.5,
            }}
            href="https://metamask.io"
            target="_blank"
            rel="noopener noreferrer"
          >
            Install MetaMask
          </Button>
        </Box>
      );
    }

    if (!isWalletConnected) {
      return (
        <Box textAlign="center" p={3}>
          <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
            Please connect your wallet to access this content
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#05e4bc',
              '&:hover': {
                backgroundColor: '#04c9a5',
              },
              borderRadius: '12px',
              textTransform: 'none',
              py: 1.5,
            }}
            onClick={this.connectWallet}
          >
            Connect Wallet
          </Button>
        </Box>
      );
    }

    if (!isCorrectChain) {
      return (
        <Box textAlign="center" p={3}>
          <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
            Please switch to the correct network to access this content
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#05e4bc',
              '&:hover': {
                backgroundColor: '#04c9a5',
              },
              borderRadius: '12px',
              textTransform: 'none',
              py: 1.5,
              mt: 2,
            }}
            onClick={this.switchChain}
          >
            Switch Network
          </Button>
          {error && (
            <Typography color="#fcfcfc" variant="body2" mt={2}>
              {error}
            </Typography>
          )}
        </Box>
      );
    }

    const currentAddress = window.ethereum.selectedAddress?.toLowerCase();
    const isOrganizer = currentAddress === daoOrganizerAddress?.toLowerCase();

    if (!isOrganizer) {
      return (
        <Box textAlign="center" p={3}>
          <Typography variant="body1" sx={{ color: '#fcfcfc' }}>
            You do not have permission to access this content. Only the DAO organizer can access this section.
          </Typography>
        </Box>
      );
    }

    return children;
  }
}

export default TabWrapper;
